import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@peacecover/component';

import { Box, SimpleGrid, Skeleton, Stack, Text, Wrap, useColorModeValue } from '@chakra-ui/react';
import { t } from 'i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import Page from '../../components/Page';
import useUserQuery from '../../components/api/queries/useUserQuery';
import RegularSolidButton from '../../components/buttons/RegularSolidButton';
import { useGetBettieBalances } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieBalances';
import { useGetBettieProducts } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieProducts';
import { useGetUserBinder } from '../../domains/userBinders/ui/hooks/useGetUserBinder';
import DiaryBookCover from './components/DiaryBookCover';
import { ENABLE_DIARY_COVER_CUSTOMIZATION } from '../../constants/photoCardDiary';
import { getArtistId, getUserId } from '@pocapan/shared/utils/etcUtils';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import PopUpBasic from '../components/popup/PopUpBasic';
import PayLetterPaymentMethodSelector from '../payment/components/PayLetterPaymentMethodSelector';
import { getOneTimePaymentLaunchPath, getPgProvider } from '@pocapan/shared/utils/paymentProvider';
import { saveData } from '@pocapan/shared/utils/saveData';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { getDiaryCover, loadDiaryCoverMap } from '@pocapan/shared/utils/photoCardDiaryCoverStorage';
import { queryGet } from '@pocapan/shared/utils/queryClient';

const DIARY_BOARD_COVER_WIDTH = 260;
const DIARY_BOARD_COVER_HEIGHT = 362;
const DIARY_BOARD_COVER_SCALE = 0.6;
const DIARY_BOARD_COVER_SCALED_WIDTH = `calc(${DIARY_BOARD_COVER_WIDTH}px * ${DIARY_BOARD_COVER_SCALE})`;
const DIARY_BOARD_COVER_SCALED_HEIGHT = `calc(${DIARY_BOARD_COVER_HEIGHT}px * ${DIARY_BOARD_COVER_SCALE})`;
const DIARY_BOARD_GRID_COLUMN_GAP = { base: '12px', md: '18px' };
const DIARY_BOARD_GRID_ROW_GAP = '26px';
const HEADER_TITLE_SCROLL_THRESHOLD = 48;
const DIARY_BOARD_SKELETON_KEYS = ['left', 'right'];

function getDiaryCoverActionState({
  isCountReady,
  memberCollectedCount,
  hasSavedCoverImage,
  isCoverTicketReady,
  hasBinderCoverTicket,
  canPurchaseCoverTicket
}) {
  const editCoverButtonText = t('myPhotoCardDiary.editCoverButton', {
    defaultValue: '커버 꾸미기'
  });

  if (!isCountReady) {
    return {
      mode: 'loading',
      label: editCoverButtonText,
      isDisabled: true
    };
  }

  if (memberCollectedCount === 0) {
    return {
      mode: 'locked',
      label: t('myPhotoCardDiary.lockedCoverButton', {
        defaultValue: '🔒 커버 꾸미기'
      }),
      isDisabled: true
    };
  }

  if (hasSavedCoverImage) {
    return {
      mode: 'edit',
      label: editCoverButtonText,
      isDisabled: false
    };
  }

  if (!isCoverTicketReady) {
    return {
      mode: 'loading',
      label: editCoverButtonText,
      isDisabled: true
    };
  }

  if (hasBinderCoverTicket) {
    return {
      mode: 'confirm-use',
      label: editCoverButtonText,
      isDisabled: false
    };
  }

  return {
    mode: 'purchase',
    label: t('myPhotoCardDiary.purchaseCoverTicketButton', {
      defaultValue: '💎 꾸미기권 구매'
    }),
    isDisabled: !canPurchaseCoverTicket
  };
}

function DiaryCoverCard({
  cover,
  coverImageUrl,
  member,
  artistName,
  badgeText,
  metaText,
  ribbonText,
  isLocked = false,
  onClick,
  actionMode,
  actionLabel,
  isActionDisabled = false,
  onActionClick
}) {
  const actionButtonColor = actionMode === 'purchase' ? 'secondary' : 'primary';

  return (
    <Stack spacing={'12px'} width={DIARY_BOARD_COVER_SCALED_WIDTH}>
      <Box
        position={'relative'}
        width={DIARY_BOARD_COVER_SCALED_WIDTH}
        height={DIARY_BOARD_COVER_SCALED_HEIGHT}
        overflow={'hidden'}
        borderTopLeftRadius={'2px'}
        borderTopRightRadius={'11px'}
        borderBottomRightRadius={'11px'}
        borderBottomLeftRadius={'2px'}
      >
        {metaText
          ? <Box
              position={'absolute'}
              top={'10px'}
              right={'-22px'}
              zIndex={2}
              width={'84px'}
              height={'20px'}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              paddingX={'0px'}
              transform={'rotate(41deg)'}
              backgroundColor={'rgba(255,255,255,0.82)'}
              borderTop={'1px solid rgba(255,255,255,0.56)'}
              borderBottom={'1px solid rgba(0,0,0,0.12)'}
              boxShadow={'0 4px 10px rgba(17,17,17,0.16)'}
              pointerEvents={'none'}
            >
              <Text
                width={'100%'}
                color={'rgba(97,102,112,0.96)'}
                fontSize={'10px'}
                fontWeight={700}
                lineHeight={1}
                letterSpacing={'-0.02em'}
                textAlign={'center'}
                textShadow={'0 1px 0 rgba(255,255,255,0.28)'}
                transform={'translateX(-3px)'}
              >
                {metaText}
              </Text>
            </Box>
          : null}

        <Box
          as="button"
          type="button"
          onClick={isLocked ? undefined : onClick}
          display={'block'}
          width={DIARY_BOARD_COVER_SCALED_WIDTH}
          height={DIARY_BOARD_COVER_SCALED_HEIGHT}
          padding={'0'}
          border={'0'}
          background={'transparent'}
          textAlign={'left'}
          cursor={isLocked ? 'default' : 'pointer'}
          aria-disabled={isLocked}
          tabIndex={isLocked ? -1 : 0}
        >
          <Box
            width={`${DIARY_BOARD_COVER_WIDTH}px`}
            height={`${DIARY_BOARD_COVER_HEIGHT}px`}
            transform={`scale(${DIARY_BOARD_COVER_SCALE})`}
            transformOrigin={'top left'}
          >
            <DiaryBookCover
              cover={cover}
              coverImageUrl={coverImageUrl}
              memberName={member.name}
              subtitle={artistName || undefined}
              ribbonText={ribbonText}
              badgeText={badgeText}
            />
          </Box>
        </Box>
      </Box>

      {actionLabel
        ? <Button
            btnColor={actionButtonColor}
            size="large"
            onClick={onActionClick}
            disabled={isActionDisabled}
            className="w-full !h-[40px] !min-h-[40px] !rounded-full text-[14px] font-semibold leading-none"
          >
            {actionLabel}
          </Button>
        : null}
    </Stack>
  );
}

function DiaryCoverCardSkeleton({ startColor, endColor }) {
  return (
    <Stack spacing={'12px'} width={DIARY_BOARD_COVER_SCALED_WIDTH}>
      <Skeleton
        width={DIARY_BOARD_COVER_SCALED_WIDTH}
        height={DIARY_BOARD_COVER_SCALED_HEIGHT}
        borderTopLeftRadius={'2px'}
        borderTopRightRadius={'11px'}
        borderBottomRightRadius={'11px'}
        borderBottomLeftRadius={'2px'}
        fadeDuration={0.12}
        startColor={startColor}
        endColor={endColor}
      />
      {ENABLE_DIARY_COVER_CUSTOMIZATION
        ? <Skeleton
            width={DIARY_BOARD_COVER_SCALED_WIDTH}
            height={'40px'}
            borderRadius={'999px'}
            fadeDuration={0.12}
            startColor={startColor}
            endColor={endColor}
          />
        : null}
    </Stack>
  );
}

export default function MyPhotoCardBoardPage() {
  const pageRef = useRef(null);
  const navigate = useNavigate();
  const { user_id: userId } = useParams();
  const [selectedArtistId, setSelectedArtistId] = useState(() => getArtistId() || '');
  const [pendingCoverUseMemberId, setPendingCoverUseMemberId] = useState('');
  const [isPayLetterMethodSelectorOpen, setIsPayLetterMethodSelectorOpen] = useState(false);
  const handleHistoryBack = useHandleHistoryBack();
  const { data: artists = [], isLoading: isArtistLoading } = useQuery(
    ['binderArtists'],
    () => queryGet('/api/v1/artists?liked_only=true'),
    {
      select: (data) => data.artists || []
    }
  );
  const selectedArtist = useMemo(() => {
    return artists.find((artist) => artist.artistId === selectedArtistId) || null;
  }, [artists, selectedArtistId]);
  const user = useUserQuery();
  const likedMembers = useMemo(() => {
    const members = selectedArtist?.members || [];
    const filteredMembers = members.filter((member) => member.isLiked);
    return filteredMembers.length > 0 ? filteredMembers : members;
  }, [selectedArtist]);
  const currentUserId = user?.userId || getUserId() || '';
  const artistName = selectedArtist?.shortName || selectedArtist?.name;
  const collectorName =
    currentUserId === userId
      ? user?.baseUserProfile?.nickname || user?.nickname || undefined
      : undefined;
  const skeletonStartColor = useColorModeValue('rgba(17,17,17,0.018)', 'rgba(255,255,255,0.045)');
  const skeletonEndColor = useColorModeValue('rgba(17,17,17,0.04)', 'rgba(255,255,255,0.085)');
  const pageTitle = t('myPhotoCardDiary.pageTitle', { defaultValue: '포토카드 바인더' });
  const pageHeading = t('myPhotoCardDiary.heading', { defaultValue: '포토카드 바인더' });
  const pageDescription = collectorName
    ? t('myPhotoCardDiary.subheadingWithNickname', {
        nickname: collectorName,
        defaultValue: '{{nickname}}님이 모은 포카를 한곳에 정리했어요.'
      })
    : t('myPhotoCardDiary.subheading', {
        defaultValue: '모은 포카를 한곳에 정리했어요.'
      });

  const [showHeaderTitle, setShowHeaderTitle] = useState(false);
  const coverMap = useMemo(() => {
    return loadDiaryCoverMap({ userId, artistId: selectedArtistId });
  }, [selectedArtistId, userId]);
  const memberIdsKey = useMemo(() => {
    return likedMembers.map((member) => member.memberId).join(',');
  }, [likedMembers]);
  const memberOrderMap = useMemo(() => {
    return Object.fromEntries(
      likedMembers.map((member, memberIndex) => [member.memberId, memberIndex])
    );
  }, [likedMembers]);
  const { data: userBinderMap = {} } = useGetUserBinder(currentUserId, userId, selectedArtistId, {
    enabled: !!currentUserId
  });
  const { data: bettieBalances, isLoading: isBettieBalancesLoading } = useGetBettieBalances({
    enabled: !!currentUserId && ENABLE_DIARY_COVER_CUSTOMIZATION
  });
  const { data: binderCoverTicketProducts = [], isLoading: isBinderCoverTicketProductsLoading } =
    useGetBettieProducts({
      category: 'onetime',
      assetType: 'binder_cover_ticket',
      enabled: !!currentUserId && ENABLE_DIARY_COVER_CUSTOMIZATION
    });
  const userBinderImageMap = useMemo(() => {
    return Object.fromEntries(
      Object.entries(userBinderMap).map(([memberId, userBinder]) => {
        return [memberId, userBinder?.imageUrl || ''];
      })
    );
  }, [userBinderMap]);
  const binderCoverTicketProduct = useMemo(() => {
    return (
      binderCoverTicketProducts.find(
        (product) => product.isActive && product.category === 'onetime'
      ) ||
      binderCoverTicketProducts.find((product) => product.isActive) ||
      null
    );
  }, [binderCoverTicketProducts]);
  const availableBinderCoverTicketCount = bettieBalances?.binderCoverTicket?.balance ?? 0;
  const hasBinderCoverTicket = availableBinderCoverTicketCount > 0;
  const isCoverTicketReady = !isBettieBalancesLoading && !isBinderCoverTicketProductsLoading;
  const pendingCoverUseMember = useMemo(() => {
    return likedMembers.find((member) => member.memberId === pendingCoverUseMemberId) || null;
  }, [likedMembers, pendingCoverUseMemberId]);

  const { data: memberCollectedCountMap = {}, isLoading: isCollectedCountLoading } = useQuery(
    ['collectedPhotoCardIds', selectedArtistId, 'binderBoard', memberIdsKey],
    async () => {
      const collectedCountEntries = await Promise.all(
        likedMembers.map(async (member) => {
          const queryParams = new URLSearchParams({
            member_id: member.memberId
          });
          const collectedPhotoCardData = await queryGet(
            `/api/v1/artists/${selectedArtistId}/photo_cards/collected_ids?${queryParams.toString()}`
          );

          return [member.memberId, collectedPhotoCardData?.collectedPhotoCardIds?.length || 0];
        })
      );

      return Object.fromEntries(collectedCountEntries);
    },
    {
      enabled: !!selectedArtistId && likedMembers.length > 0
    }
  );
  const sortedMembers = useMemo(() => {
    return [...likedMembers].sort((leftMember, rightMember) => {
      const leftCount = memberCollectedCountMap[leftMember.memberId];
      const rightCount = memberCollectedCountMap[rightMember.memberId];
      const resolvedLeftCount = typeof leftCount === 'number' ? leftCount : -1;
      const resolvedRightCount = typeof rightCount === 'number' ? rightCount : -1;

      if (resolvedLeftCount !== resolvedRightCount) {
        return resolvedRightCount - resolvedLeftCount;
      }

      return (
        (memberOrderMap[leftMember.memberId] ?? Number.MAX_SAFE_INTEGER) -
        (memberOrderMap[rightMember.memberId] ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }, [likedMembers, memberCollectedCountMap, memberOrderMap]);
  const handlePurchaseCoverTicket = useCallback(
    (paymentMethod) => {
      if (!binderCoverTicketProduct || !user) return;

      const amount = Number(
        binderCoverTicketProduct.baseAmount - binderCoverTicketProduct.discountAmount
      );
      const encodedProductName = encodeURIComponent(binderCoverTicketProduct.name);
      const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
      const isPayLetter = getPgProvider() === 'pay-letter';

      if (user.identified) {
        if (isPayLetter && !paymentMethod) {
          setIsPayLetterMethodSelectorOpen(true);
          return;
        }

        openNewTab(
          `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${binderCoverTicketProduct.bettieProductId}${paymentMethod ? `&paymentMethod=${paymentMethod}` : ''}`
        );
        return;
      }

      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${binderCoverTicketProduct.bettieProductId}`
      );
    },
    [binderCoverTicketProduct, user]
  );
  const handleConfirmCoverUse = useCallback(() => {
    if (!pendingCoverUseMemberId) return;

    setPendingCoverUseMemberId('');
    navigate(`/users/${userId}/binder/${pendingCoverUseMemberId}/cover`);
  }, [navigate, pendingCoverUseMemberId, userId]);

  useEffect(() => {
    if (artists.length === 0) return;

    const nextArtist = selectedArtist || artists[0];
    if (!nextArtist || nextArtist.artistId === selectedArtistId) return;

    saveData('artist', nextArtist.artistId);
    setSelectedArtistId(nextArtist.artistId);
  }, [artists, selectedArtist, selectedArtistId]);

  useEffect(() => {
    const container = pageRef.current;

    const updateHeaderTitleVisibility = () => {
      const containerScrollTop = container?.scrollTop || 0;
      const windowScrollTop =
        window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const scrollTop = Math.max(containerScrollTop, windowScrollTop);

      setShowHeaderTitle(scrollTop > HEADER_TITLE_SCROLL_THRESHOLD);
    };

    updateHeaderTitleVisibility();

    container?.addEventListener('scroll', updateHeaderTitleVisibility);
    window.addEventListener('scroll', updateHeaderTitleVisibility);

    return () => {
      container?.removeEventListener('scroll', updateHeaderTitleVisibility);
      window.removeEventListener('scroll', updateHeaderTitleVisibility);
    };
  }, []);

  const handleArtistSelect = (artist) => {
    if (!artist || artist.artistId === selectedArtistId) return;

    saveData('artist', artist.artistId);
    startTransition(() => {
      setSelectedArtistId(artist.artistId);
    });
  };

  const isBoardLoading = isArtistLoading && artists.length === 0;
  const hasArtists = artists.length > 0;
  const hasMultipleArtists = artists.length > 1;

  return (
    <Page
      ref={pageRef}
      title={pageTitle}
      showTitle={showHeaderTitle}
      showBackButton={true}
      onHistoryBackClick={handleHistoryBack}
    >
      <Box
        display={'flex'}
        flexDirection={'column'}
        minHeight={'calc((var(--vh, 1vh) * 100) - (44px + env(safe-area-inset-top)) - 48px)'}
      >
        <Stack spacing={'24px'} flex={1}>
          <Stack spacing={'6px'} paddingTop={'12px'} paddingBottom={'12px'} alignItems={'center'}>
            <Text
              className="text-main-light-1 dark:text-main-dark-1"
              fontSize={'28px'}
              fontWeight={700}
              textAlign={'center'}
            >
              {pageHeading}
            </Text>
            <Text
              className="text-main-light-3 dark:text-main-dark-3"
              fontSize={'15px'}
              textAlign={'center'}
            >
              {pageDescription}
            </Text>
          </Stack>

          {!isBoardLoading && hasMultipleArtists && (
            <Wrap spacing={'8px'} justify={'flex-start'} width={'100%'}>
              {artists.map((artist) => (
                <RegularSolidButton
                  key={artist.artistId}
                  text={artist.shortName || artist.name}
                  filled={artist.artistId === selectedArtistId}
                  onClicked={() => handleArtistSelect(artist)}
                />
              ))}
            </Wrap>
          )}

          {!isBoardLoading && hasArtists && isCollectedCountLoading && (
            <Text
              className="text-main-light-3 dark:text-main-dark-3"
              fontSize={'13px'}
              textAlign={'center'}
            >
              {t('myPhotoCardDiary.loadingMeta', {
                defaultValue: '정보를 확인하는 중...'
              })}
            </Text>
          )}

          {isBoardLoading
            ? <SimpleGrid
                columns={{ base: 2, md: 2 }}
                columnGap={DIARY_BOARD_GRID_COLUMN_GAP}
                rowGap={DIARY_BOARD_GRID_ROW_GAP}
                justifyItems={'center'}
              >
                {DIARY_BOARD_SKELETON_KEYS.map((key) => (
                  <DiaryCoverCardSkeleton
                    key={key}
                    startColor={skeletonStartColor}
                    endColor={skeletonEndColor}
                  />
                ))}
              </SimpleGrid>
            : hasArtists
              ? <SimpleGrid
                  columns={{ base: 2, md: 2 }}
                  columnGap={DIARY_BOARD_GRID_COLUMN_GAP}
                  rowGap={DIARY_BOARD_GRID_ROW_GAP}
                  justifyItems={'center'}
                >
                  {sortedMembers.map((member) => {
                    const cover = getDiaryCover({
                      coverMap,
                      member,
                      memberIndex: memberOrderMap[member.memberId] ?? 0
                    });
                    const memberCollectedCount = Object.hasOwn(
                      memberCollectedCountMap,
                      member.memberId
                    )
                      ? memberCollectedCountMap[member.memberId]
                      : undefined;
                    const isCountReady = typeof memberCollectedCount === 'number';
                    const memberCollectedCountText =
                      typeof memberCollectedCount === 'number' && memberCollectedCount > 0
                        ? t('myPhotoCardDiary.coverCount', {
                            count: memberCollectedCount
                          })
                        : undefined;
                    const hasSavedCoverImage = !!userBinderImageMap[member.memberId];
                    const actionState = getDiaryCoverActionState({
                      isCountReady,
                      memberCollectedCount,
                      hasSavedCoverImage,
                      isCoverTicketReady,
                      hasBinderCoverTicket,
                      canPurchaseCoverTicket: !!binderCoverTicketProduct && !!user
                    });
                    const emptyBinderRibbonText =
                      memberCollectedCount === 0
                        ? t('myPhotoCardDiary.emptyBinderRibbon', {
                            defaultValue: '아직 모은 포카가 없어 열 수 없어요'
                          })
                        : undefined;
                    const handleActionClick =
                      actionState.mode === 'edit'
                        ? () => navigate(`/users/${userId}/binder/${member.memberId}/cover`)
                        : actionState.mode === 'confirm-use'
                          ? () => setPendingCoverUseMemberId(member.memberId)
                          : actionState.mode === 'purchase'
                            ? () => handlePurchaseCoverTicket()
                            : undefined;

                    return (
                      <DiaryCoverCard
                        key={member.memberId}
                        member={member}
                        cover={cover}
                        coverImageUrl={userBinderImageMap[member.memberId]}
                        artistName={artistName}
                        metaText={memberCollectedCountText}
                        ribbonText={emptyBinderRibbonText}
                        isLocked={isCountReady && memberCollectedCount === 0}
                        badgeText={collectorName}
                        onClick={() => navigate(`/users/${userId}/binder/${member.memberId}`)}
                        actionMode={actionState.mode}
                        actionLabel={
                          ENABLE_DIARY_COVER_CUSTOMIZATION ? actionState.label : undefined
                        }
                        isActionDisabled={actionState.isDisabled}
                        onActionClick={handleActionClick}
                      />
                    );
                  })}
                </SimpleGrid>
              : null}

          {!isBoardLoading && !hasArtists && (
            <Box
              borderRadius={'24px'}
              padding={'24px'}
              className="bg-surface-light dark:bg-backgroundElevated-dark"
            >
              <Text className="text-main-light-3 dark:text-main-dark-3">
                {t('myPhotoCardDiary.emptyArtists', {
                  defaultValue: '좋아요한 아티스트가 없어요.'
                })}
              </Text>
            </Box>
          )}

          {!isBoardLoading && hasArtists && likedMembers.length === 0 && (
            <Box
              borderRadius={'24px'}
              padding={'24px'}
              className="bg-surface-light dark:bg-backgroundElevated-dark"
            >
              <Text className="text-main-light-3 dark:text-main-dark-3">
                {t('myPhotoCardDiary.emptyMembers', {
                  defaultValue: '아직 다이어리로 열 멤버가 없어요.'
                })}
              </Text>
            </Box>
          )}
        </Stack>

        <Box marginTop={'auto'} paddingTop={'32px'}>
          <Box
            marginX={'-24px'}
            marginBottom={'-24px'}
            paddingX={'24px'}
            paddingTop={'16px'}
            paddingBottom={'calc(18px + env(safe-area-inset-bottom))'}
            backgroundColor={'rgba(17,17,17,0.025)'}
            boxShadow={'inset 0 1px 0 rgba(255,255,255,0.22)'}
          >
            <Stack spacing={'8px'} width={'100%'} alignItems={'stretch'}>
              <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
                <Box
                  width={'2px'}
                  height={'2px'}
                  marginTop={'7px'}
                  borderRadius={'999px'}
                  backgroundColor={'currentColor'}
                  opacity={0.64}
                  flexShrink={0}
                />
                <Text
                  className="text-main-light-3 dark:text-main-dark-3"
                  fontSize={'12px'}
                  textAlign={'left'}
                  lineHeight={'18px'}
                >
                  {t('myPhotoCardDiary.footerNoticeMembers', {
                    defaultValue: '최애가 늘어나면 바인더도 함께 늘어나요'
                  })}
                </Text>
              </Box>

              <Box display={'flex'} alignItems={'flex-start'} gap={'8px'}>
                <Box
                  width={'2px'}
                  height={'2px'}
                  marginTop={'7px'}
                  borderRadius={'999px'}
                  backgroundColor={'currentColor'}
                  opacity={0.64}
                  flexShrink={0}
                />
                <Text
                  className="text-main-light-3 dark:text-main-dark-3"
                  fontSize={'12px'}
                  textAlign={'left'}
                  lineHeight={'18px'}
                >
                  {t('myPhotoCardDiary.footerNoticeArtist', {
                    defaultValue: '아티스트 전체 바인더는 현재 지원하지 않아요'
                  })}
                </Text>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      {pendingCoverUseMemberId
        ? <div className="fixed inset-0 z-[1400] flex items-center justify-center overflow-y-auto bg-[rgba(0,0,0,0.5)] px-[24px] py-[24px]">
            <PopUpBasic
              type="short"
              title={t('myPhotoCardDiary.useCoverTicketTitle', {
                defaultValue: '이 바인더에 꾸미기권을 1장을 사용할까요?'
              })}
              description={t('myPhotoCardDiary.useCoverTicketDescription', {
                memberName: pendingCoverUseMember?.name || '',
                defaultValue: '꾸미기권 1장을 사용하면,\n이 바인더는 자유롭게 계속 꾸밀 수 있어요.'
              })}
              content={
                <div className="rounded-[16px] border border-[rgba(17,17,17,0.06)] bg-[rgba(17,17,17,0.04)] px-[16px] py-[12px]">
                  <div className="flex items-center justify-between gap-[12px]">
                    <p className="text-[13px] font-medium leading-[16px] text-text-main-lv3">
                      {t('myPhotoCardDiary.coverTicketBalanceLabel', {
                        defaultValue: '보유한 커버 꾸미기권'
                      })}
                    </p>
                    <p className="text-[15px] font-bold leading-[18px] text-text-main-lv1">
                      {t('myPhotoCardDiary.coverTicketBalanceCount', {
                        count: availableBinderCoverTicketCount,
                        defaultValue: '{{count}}장'
                      })}
                    </p>
                  </div>
                </div>
              }
              primaryButtonText={t('cancel', { defaultValue: '취소' })}
              secondaryButtonText={t('myPhotoCardDiary.useCoverTicketButton', {
                defaultValue: '사용'
              })}
              onPrimaryButtonClick={() => setPendingCoverUseMemberId('')}
              onSecondaryButtonClick={handleConfirmCoverUse}
              className="z-[1401] w-full max-w-[360px] !min-h-fit !mb-0"
            />
          </div>
        : null}

      <PayLetterPaymentMethodSelector
        isOpen={isPayLetterMethodSelectorOpen}
        onClose={() => setIsPayLetterMethodSelectorOpen(false)}
        onSelect={(paymentMethod) => {
          setIsPayLetterMethodSelectorOpen(false);
          handlePurchaseCoverTicket(paymentMethod);
        }}
      />
    </Page>
  );
}
